/**
 * Synchronous cross-module execution gate.
 * React context alone is not enough: Test Plan execute awaits before its lock check,
 * so a Collections run started during that await can be missed (stale closure).
 */

export const ANOTHER_TEST_PLAN_RUNNING_MESSAGE =
  'Another test plan is currently running. Please wait for it to finish before starting a new one.';

export const THIS_TEST_PLAN_ALREADY_RUNNING_MESSAGE =
  'This test plan is already running. Please wait for it to finish or stop it before starting again.';

export const COLLECTION_RUNNING_IN_COLLECTIONS_MESSAGE =
  'This collection is currently running from the Collections module. Please wait for it to finish before executing the Test Plan.';

export const COLLECTION_RUNNING_IN_TEST_PLAN_MESSAGE =
  'This collection is currently running as part of a Test Plan. Please wait for the Test Plan to finish before executing it from Collections.';

export const ANOTHER_COLLECTION_RUNNING_MESSAGE =
  'Another collection is running. Please wait until it is finished.';

export const COLLECTION_ALREADY_RUNNING_MESSAGE =
  'This collection is already running. Please wait until it is finished.';

export type ExecutionGateHolder =
  | {
      source: 'collection';
      rootCollectionId: number;
      collectionIds: number[];
    }
  | {
      source: 'testPlan';
      cycleId: number;
      collectionIds: number[];
    };

let holder: ExecutionGateHolder | null = null;

function positiveUniqueIds(ids: Array<number | null | undefined> | undefined | null): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const id of ids ?? []) {
    if (typeof id !== 'number' || !Number.isFinite(id) || id <= 0) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function getExecutionGateHolder(): ExecutionGateHolder | null {
  return holder;
}

export function collectionIdsOverlap(a: number[], b: number[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const setB = new Set(b);
  return a.some((id) => setB.has(id));
}

function blockReasonForTestPlan(
  cycleId: number,
  _planCollectionIds: number[],
  current: ExecutionGateHolder
): string {
  if (current.source === 'testPlan') {
    if (current.cycleId === cycleId) return THIS_TEST_PLAN_ALREADY_RUNNING_MESSAGE;
    return ANOTHER_TEST_PLAN_RUNNING_MESSAGE;
  }
  // Any Collections-module run blocks Test Plan starts (avoids lock steal / dual runs).
  return COLLECTION_RUNNING_IN_COLLECTIONS_MESSAGE;
}

function blockReasonForCollection(
  collectionIds: number[],
  current: ExecutionGateHolder
): string {
  if (current.source === 'testPlan') {
    // Any Test Plan run blocks Collections-module starts.
    return COLLECTION_RUNNING_IN_TEST_PLAN_MESSAGE;
  }
  if (collectionIdsOverlap(current.collectionIds, collectionIds)) {
    return COLLECTION_ALREADY_RUNNING_MESSAGE;
  }
  return ANOTHER_COLLECTION_RUNNING_MESSAGE;
}

/** Peek without acquiring — safe after awaits to re-validate. */
export function getTestPlanGateBlockReason(
  cycleId: number,
  planCollectionIds: Array<number | null | undefined>
): string | null {
  if (!holder) return null;
  const ids = positiveUniqueIds(planCollectionIds);
  const reason = blockReasonForTestPlan(cycleId, ids, holder);
  return reason || null;
}

export function getCollectionGateBlockReason(
  collectionIds: Array<number | null | undefined>
): string | null {
  if (!holder) return null;
  const ids = positiveUniqueIds(collectionIds);
  if (ids.length === 0) return null;
  const reason = blockReasonForCollection(ids, holder);
  return reason || null;
}

/**
 * Acquire Test Plan lock synchronously. Returns error message if blocked.
 * Call before any await. Not idempotent — a second acquire for the same cycle is blocked.
 */
export function tryAcquireTestPlanExecution(
  cycleId: number,
  planCollectionIds: Array<number | null | undefined>
): string | null {
  const ids = positiveUniqueIds(planCollectionIds);
  if (holder) {
    const reason = blockReasonForTestPlan(cycleId, ids, holder);
    if (reason) return reason;
  }
  holder = { source: 'testPlan', cycleId, collectionIds: ids };
  return null;
}

export function tryAcquireCollectionExecution(
  rootCollectionId: number,
  collectionIds: Array<number | null | undefined>
): string | null {
  const ids = positiveUniqueIds(
    (collectionIds?.length ? collectionIds : null) ?? [rootCollectionId]
  );
  if (ids.length === 0 && rootCollectionId > 0) ids.push(rootCollectionId);
  if (holder) {
    const reason = blockReasonForCollection(ids, holder);
    if (reason) return reason;
  }
  holder = { source: 'collection', rootCollectionId, collectionIds: ids };
  return null;
}

/** Update collection IDs on an already-held Test Plan lock (e.g. after enrich). */
export function refreshTestPlanExecutionCollections(
  cycleId: number,
  planCollectionIds: Array<number | null | undefined>
): void {
  if (holder?.source === 'testPlan' && holder.cycleId === cycleId) {
    const ids = positiveUniqueIds(planCollectionIds);
    if (ids.length > 0) holder = { ...holder, collectionIds: ids };
  }
}

export function isTestPlanExecutionHeld(cycleId: number): boolean {
  return holder?.source === 'testPlan' && holder.cycleId === cycleId;
}

export function isCollectionExecutionHeld(rootCollectionId: number): boolean {
  return holder?.source === 'collection' && holder.rootCollectionId === rootCollectionId;
}

export function releaseTestPlanExecution(cycleId: number): void {
  if (holder?.source === 'testPlan' && holder.cycleId === cycleId) {
    holder = null;
  }
}

export function releaseCollectionExecution(rootCollectionId: number): void {
  if (holder?.source === 'collection' && holder.rootCollectionId === rootCollectionId) {
    holder = null;
  }
}

/** Test-only reset. */
export function __resetExecutionGateForTests(): void {
  holder = null;
}
